/**
 * SprintCompleteModal Component
 * AIDLC Administrative Backend
 *
 * Modal for completing an active sprint. Collects retrospective notes
 * (what went well, what to improve, action items) and triggers velocity calc.
 */

import { useState } from 'react';
import { Modal } from '../../shared/Modal';
import { Button } from '../../shared/Button';
import { completeSprint } from '../../../services/sprintService';
import type { Sprint } from '../../../types/projects';

interface SprintCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  sprint: Sprint;
  onCompleted: (sprint: Sprint) => void;
}

export function SprintCompleteModal({ isOpen, onClose, sprint, onCompleted }: SprintCompleteModalProps) {
  const [wellText, setWellText] = useState('');
  const [improveText, setImproveText] = useState('');
  const [actionsText, setActionsText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleComplete = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Parse multi-line text into arrays (one item per line)
      const retrospective = {
        whatWentWell: wellText.split('\n').map((s) => s.trim()).filter(Boolean),
        whatToImprove: improveText.split('\n').map((s) => s.trim()).filter(Boolean),
        actionItems: actionsText.split('\n').map((s) => s.trim()).filter(Boolean),
      };
      const completed = await completeSprint(sprint.id, retrospective);
      onCompleted(completed);
      // Reset form
      setWellText('');
      setImproveText('');
      setActionsText('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete sprint');
    } finally {
      setIsSubmitting(false);
    }
  };

  const completedCount = sprint.backlogItemIds.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Complete Sprint: ${sprint.name}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleComplete}
            isLoading={isSubmitting}
          >
            Complete Sprint
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-xs">{error}</p>
          </div>
        )}

        {/* Sprint summary */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Sprint Summary</div>
          <div className="text-sm font-medium text-gray-800 mt-1">{sprint.goal || 'No goal set'}</div>
          <div className="text-xs text-gray-500 mt-1">
            {completedCount} item{completedCount !== 1 ? 's' : ''} in sprint |{' '}
            {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
          </div>
        </div>

        {/* Retrospective fields */}
        <div>
          <label className="block text-xs font-medium text-green-700 mb-1">
            What went well? (one item per line)
          </label>
          <textarea
            value={wellText}
            onChange={(e) => setWellText(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500/30 bg-green-50/30"
            placeholder="Team velocity improved&#10;Auth module delivered ahead of schedule&#10;Code review process worked smoothly"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-yellow-700 mb-1">
            What could be improved? (one item per line)
          </label>
          <textarea
            value={improveText}
            onChange={(e) => setImproveText(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-yellow-200 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500/30 bg-yellow-50/30"
            placeholder="Too many items pulled into sprint&#10;Testing started too late&#10;Cross-team communication gaps"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-ldaja-blue mb-1">
            Action items for next sprint (one item per line)
          </label>
          <textarea
            value={actionsText}
            onChange={(e) => setActionsText(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 bg-blue-50/30"
            placeholder="Limit sprint to 8 items&#10;Start testing from day 3&#10;Daily async standup in chat"
          />
        </div>
      </div>
    </Modal>
  );
}
